import React from 'react';
import { motion } from 'framer-motion';
import { MessageCircle } from 'lucide-react';
import { Logo } from '../components/Logo';
function DashedDivider() {
  return <div className="max-w-3xl mx-auto px-6" data-id="element-60">
      <div className="border-t border-dashed border-bg-primary/15" data-id="element-61" />
    </div>;
}
function FadeUp({
  children,
  delay = 0
}: {
  children: React.ReactNode;
  delay?: number;
}) {
  return <motion.div initial={{
    opacity: 0,
    y: 16
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-50px'
  }} transition={{
    duration: 0.4,
    ease: 'easeOut',
    delay
  }} data-id="element-62">
      {children}
    </motion.div>;
}
const qualifiers = ["You're a $250M+ producer at a wirehouse or captive team — Merrill, Morgan Stanley, UBS, Wells, JPM Private Bank — and the last comp grid update was the third paper cut in a row.", "Your firm just got bought, restructured, or rolled into something with a different name — and the place you signed up for isn't the place you'll be working at next year.", "Fifteen-plus years in, you've built a book worth millions, you keep roughly forty cents on the dollar, and you own none of it — and that math has stopped sitting right with you.", "You'd already be gone if it weren't for the TRO risk, the deferred comp you'd forfeit, and the operational headache of going independent — and you want someone who's done this before to handle that part, quietly.", "You're the top producer on your team but you're being managed by someone who has never sat across from a client, and the politics have started to cost you.", "You've watched peers make the move to independence and double their take-home — and you're tired of telling yourself you'll look into it next year.", "You serve ultra-high-net-worth families who deserve a fiduciary, but your firm keeps pushing you to operate under a suitability standard and cross-sell products that don't serve them.", "You're not looking to build an RIA from scratch. You want to plug into the right firm, keep your clients, and get paid what your book is actually worth."];
export function ThankYou() {
  return <div className="min-h-screen bg-white text-bg-primary font-sans selection:bg-accent selection:text-bg-primary" data-id="element-63">
      {/* Centered logo */}
      <div className="w-full flex justify-center pt-12 md:pt-16 pb-12" data-id="element-64">
        <Logo height={40} data-id="element-65" />
      </div>

      {/* 1. Confirmation Headline */}
      <section className="w-full px-6 md:px-12 pb-16 md:pb-24" data-id="element-66">
        <div className="max-w-3xl mx-auto text-center" data-id="element-67">
          <FadeUp data-id="element-68">
            <span className="inline-block text-xs md:text-sm font-semibold tracking-[0.2em] uppercase text-bg-primary/60 mb-6" data-id="element-69">
              Session Tentatively Reserved
            </span>
          </FadeUp>
          <FadeUp delay={0.05} data-id="element-70">
            <h1 className="font-display font-extrabold text-[36px] md:text-[52px] leading-[1.1] tracking-tightest text-bg-primary mb-8" data-id="element-71">
              Your Strategy Session Is Tentatively Held.
            </h1>
          </FadeUp>
          <FadeUp delay={0.1} data-id="element-72">
            <p className="text-lg md:text-xl leading-relaxed text-bg-primary/70 max-w-2xl mx-auto" data-id="element-73">
              We hold each session for one founder at a time. To lock in your
              spot, we'll text you in the next hour to confirm a few details —
              please reply when you see it so we can finalize the time.
            </p>
          </FadeUp>
        </div>
      </section>

      <DashedDivider data-id="element-74" />

      {/* 3. Text/Email Confirmation */}
      <section className="w-full px-6 md:px-12 py-20 md:py-28" data-id="element-75">
        <div className="max-w-3xl mx-auto" data-id="element-76">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16 items-start" data-id="element-77">
            <FadeUp data-id="element-78">
              <span className="block text-xs md:text-sm font-semibold tracking-[0.2em] uppercase text-bg-primary/60 mb-6" data-id="element-79">
                One Quick Step
              </span>
              <h2 className="font-display font-bold text-[28px] md:text-[36px] leading-tight tracking-tightest text-bg-primary mb-6" data-id="element-80">
                We'll Text You To Confirm.
              </h2>
              <p className="text-bg-primary/70 text-[17px] leading-relaxed mb-5" data-id="element-81">
                We personally confirm every session before it goes on the
                calendar. It's a small step that keeps our sessions reserved for
                founders who are serious about understanding their firm's
                position — and protects your time from the noise.
              </p>
              <p className="text-bg-primary/70 text-[17px] leading-relaxed" data-id="element-82">
                You'll receive a brief text from our team within the hour. A
                one-word reply is all we need.
              </p>
            </FadeUp>

            <FadeUp delay={0.1} data-id="element-83">
              <div className="bg-bg-primary/[0.03] border border-bg-primary/10 rounded-2xl p-6 md:p-7" data-id="element-84">
                <div className="flex items-center gap-2 mb-5 pb-4 border-b border-bg-primary/10" data-id="element-85">
                  <MessageCircle className="w-4 h-4 text-bg-primary/60" data-id="element-86" />
                  <span className="text-xs uppercase tracking-widest text-bg-primary/60 font-semibold" data-id="element-87">
                    Messages
                  </span>
                </div>
                <div className="flex flex-col gap-3" data-id="element-88">
                  <div className="max-w-[85%]" data-id="element-89">
                    <div className="bg-bg-primary/[0.06] border border-bg-primary/10 rounded-2xl rounded-tl-sm px-4 py-3 text-[14px] text-bg-primary leading-snug" data-id="element-90">
                      Hi [Name] — just confirming our strategy session [Day] at
                      [Time] PT. Reply YES to lock it in.
                    </div>
                    <div className="text-[11px] text-bg-primary/50 mt-1 ml-1" data-id="element-91">
                      RIA Catalyst
                    </div>
                  </div>
                  <div className="max-w-[85%] self-end" data-id="element-92">
                    <div className="bg-accent text-bg-primary rounded-2xl rounded-tr-sm px-4 py-3 text-[14px] font-semibold" data-id="element-93">
                      Yes
                    </div>
                  </div>
                  <div className="max-w-[85%]" data-id="element-94">
                    <div className="bg-bg-primary/[0.06] border border-bg-primary/10 rounded-2xl rounded-tl-sm px-4 py-3 text-[14px] text-bg-primary leading-snug" data-id="element-95">
                      Perfect. Calendar invite on the way.
                    </div>
                    <div className="text-[11px] text-bg-primary/50 mt-1 ml-1" data-id="element-96">
                      RIA Catalyst
                    </div>
                  </div>
                </div>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      <DashedDivider data-id="element-97" />

      {/* 5. Who This Is For */}
      <section className="w-full px-6 md:px-12 py-20 md:py-28" data-id="element-98">
        <div className="max-w-3xl mx-auto" data-id="element-99">
          <FadeUp data-id="element-100">
            <h2 className="font-display font-bold text-[28px] md:text-[36px] leading-tight tracking-tightest text-bg-primary mb-12" data-id="element-101">
              Does This Describe You?
            </h2>
          </FadeUp>
          <ul className="flex flex-col gap-5" data-id="element-102">
            {qualifiers.map((q, i) => <FadeUp key={i} delay={0.05 * i} data-id="element-103">
                <li className="flex items-start gap-4 text-bg-primary/80 text-lg leading-relaxed" data-id="element-104">
                  <span className="text-accent text-xl leading-none mt-1.5 shrink-0 font-bold" data-id="element-105">
                    —
                  </span>
                  <span data-id="element-106">{q}</span>
                </li>
              </FadeUp>)}
          </ul>
          <FadeUp delay={0.1} data-id="element-107">
            <p className="text-bg-primary text-lg md:text-xl font-semibold mt-12 pt-8 border-t border-bg-primary/15" data-id="element-108">
              If any of these describe you, this call you've scheduled is worth
              taking.
            </p>
          </FadeUp>
        </div>
      </section>

      <DashedDivider data-id="element-109" />

      {/* 7. Closing Note */}
      <section className="w-full px-6 md:px-12 py-20 md:py-28" data-id="element-110">
        <div className="max-w-2xl mx-auto" data-id="element-111">
          <FadeUp data-id="element-112">
            <span className="block text-xs md:text-sm font-semibold tracking-[0.2em] uppercase text-bg-primary/60 mb-6" data-id="element-113">
              A Note From The Founder
            </span>
          </FadeUp>
          <FadeUp delay={0.05} data-id="element-114">
            <div className="text-bg-primary/80 text-lg md:text-xl leading-relaxed space-y-5" data-id="element-115">
              <p data-id="element-116">The call is worth taking whether you ever sell or not.</p>
              <p data-id="element-117">
                I spent years inside some of the largest RIAs in the country —
                at Certuity, Cresset, and IEQ Capital — and the one thing I saw
                consistently was founders making the most important financial
                decision of their lives without the right information in front
                of them.
              </p>
              <p data-id="element-118">
                Most of the founders I talk to walk away from our first
                conversation with two or three things they didn't know about
                their own firm's market position — and a clearer sense of
                whether the next five years should look different.
              </p>
              <p data-id="element-119">
                We're not here to push you toward a deal. We're here to make
                sure that if the moment ever comes, you've already done the
                thinking.
              </p>
              <div className="flex items-center gap-4 pt-4" data-id="element-120">
                <img src="https://cdn.magicpatterns.com/uploads/kATVZXPMHxUDmrz9E3ZzU7/pasted-image.png" alt="Julien Baneux, Founder & CEO of RIA Catalyst" className="w-14 h-14 md:w-16 md:h-16 rounded-full object-cover shrink-0" data-id="element-121" />
                <div className="flex flex-col" data-id="element-122">
                  <span className="text-bg-primary font-semibold text-base md:text-lg leading-tight" data-id="element-123">
                    — Julien Baneux
                  </span>
                  <span className="text-bg-primary/60 text-sm md:text-base leading-tight" data-id="element-124">
                    Founder & CEO, RIA Catalyst
                  </span>
                </div>
              </div>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* Footer */}
      <footer className="w-full px-6 md:px-12 pb-12 pt-8" data-id="element-125">
        <p className="text-center text-[12px] text-bg-primary/50" data-id="element-126">
          RIA Catalyst © 2026
        </p>
      </footer>
    </div>;
}